import React, {
  useRef,
  ReactNode,
  ButtonHTMLAttributes,
  HTMLAttributes,
  ReactElement,
} from 'react'
import { ChevronDown } from '@stratascratch/icons'
import useListenOutsideClick from './useListenOutsideClick'
import { cn } from '../lib/utils'
import './Dropdown.css'

interface DropdownProps {
  children: ReactNode
}

function Dropdown({ children }: DropdownProps) {
  const containerRef = useRef(null)
  const [showDropdown, setShowDropdown] = useListenOutsideClick(containerRef)

  const toggleMenu = () => {
    setShowDropdown(!showDropdown)
  }

  const closeMenu = () => {
    setShowDropdown(false)
  }

  const toggle = React.Children.toArray(children).find(
    (el) => React.isValidElement(el) && el.type === Dropdown.Toggle,
  )

  if (!toggle || !React.isValidElement(toggle)) {
    throw Error('Dropdown must contain a Dropdown.Toggle')
  }

  const menu = React.Children.toArray(children).find(
    (el) => React.isValidElement(el) && el.type === Dropdown.Menu,
  )

  if (!menu || !React.isValidElement(menu)) {
    throw Error('Dropdown must contain a Dropdown.Menu')
  }

  return (
    <div ref={containerRef} className="Dropdown__container">
      {React.cloneElement(toggle as ReactElement, {
        toggleMenu,
        showMenu: showDropdown,
      })}
      {showDropdown && React.cloneElement(menu as ReactElement, { closeMenu })}
    </div>
  )
}

type ToggleFn = () => void

interface ToggleProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children: ReactNode | ((showMenu: boolean, toggleMenu: ToggleFn) => ReactNode)
  // showMenu and toggleMenu are set internally and are not expected to be set by the end user
  showMenu?: boolean
  toggleMenu?: ToggleFn
  noIcon?: boolean
  className?: string
}

Dropdown.Toggle = function Toggle({
  children,
  showMenu = undefined,
  toggleMenu = undefined,
  className = '',
  noIcon = false,
  ...props
}: ToggleProps) {
  if (typeof children === 'function') {
    if (showMenu !== undefined && toggleMenu !== undefined) {
      return <>{children(showMenu, toggleMenu)}</>
    }
    throw Error(
      'A custom toggle required a render function with the correct signature',
    )
  }

  return (
    <button
      type="button"
      onClick={toggleMenu}
      {...props}
      className={`Dropdown__toggle ${
        showMenu ? 'Dropdown__toggle--active' : ''
      } ${className || ''}`}
    >
      {children}
      {!noIcon && (
        <ChevronDown
          className={`Dropdown__chevron ${
            showMenu ? 'Dropdown__chevron--active' : ''
          } ${props.disabled ? 'Dropdown__chevron--disabled' : ''}`}
        />
      )}
    </button>
  )
}

interface MenuProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  children: ReactNode | ((closeMenu: ToggleFn) => ReactNode)
  /** closeMenu is set internally and is not expected to be set by the end user */
  closeMenu?: ToggleFn
}

Dropdown.Menu = function Menu({
  children,
  closeMenu = undefined,
  className,
  ...rest
}: MenuProps) {
  if (typeof children === 'function') {
    if (closeMenu === undefined) {
      throw Error(
        'Dropdown.Menu must be a render function with the correct signature',
      )
    }

    return (
      <div
        className={cn(
          'absolute left-0 top-full z-10 mt-1 w-max rounded-md bg-[var(--bg-color)] p-0 shadow',
          className,
        )}
        {...rest}
      >
        {children(closeMenu)}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'absolute left-0 top-full z-10 mt-1 w-max rounded-md bg-[var(--bg-color)] p-0 shadow',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

export default Dropdown
